import { useEffect, useState } from 'react';

import queryString from 'query-string';
import { clone, dropLast, isEmpty, isNil, last, uniq } from 'ramda';
import { useQueryParam } from 'use-query-params';

import { defaultValue } from 'components/shared/LandingPage/contexts/LandingPageContext';
import { ACCOUNT_TYPE_AGENT, ACCOUNT_TYPE_OWNER } from 'constants/index';
import { isCheckSubscriptionPermissions, PERMISSION_REMOVE_BRANDING } from 'hooks/useIsSubscriptionAllowed';
import { useLoadTranslations } from 'hooks/useLoadTranslations';
import axios from 'utils/axios';
import { isAccordionItemDefined } from 'utils/blocksHelpers/isAccordionItemDefined';
import { isAnyAccordionItemDefined } from 'utils/blocksHelpers/isAnyAccordionItemDefined';
import { isAnyFastFactItemDefined } from 'utils/blocksHelpers/isAnyFastFactItemDefined';
import { isFastFactItemDefined } from 'utils/blocksHelpers/isFastFactDefined';
import { isTextBlockDefined } from 'utils/blocksHelpers/isTextBlockDefined';
import { getVisibleProposalPages } from 'utils/proposals';
import { isDefined } from 'utils/ramda';

import { BASIC_BLOCK_TYPES } from '../../shared/LandingPage/blocks/constants';

export function usePropertyPortal() {
  const [pageId] = useQueryParam('id');
  const [accountId] = useQueryParam('acid');
  const isInvalidPageId = isNil(pageId);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!isInvalidPageId);
  const [error, setError] = useState(isInvalidPageId ? 'Invalid page id' : null);
  useLoadTranslations();

  useEffect(() => {
    if (isInvalidPageId) return;

    const run = async () => {
      try {
        const response = await axios.get(`/pages/${pageId}?acid=${accountId}`);
        if (!response.data) {
          throw new Error('Invalid data received');
        }

        setData(getLandingForViewMode(response.data));
      } catch (error) {
        setError(error);
      } finally {
        setLoading(false);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageId]);

  return {
    data,
    loading,
    error,
  };
}

export function usePropertyPortalContextValues({ landingData, page, links }) {
  const { type } = queryString.parse(window.location.search);
  const [showAgentContactsModal, setShowAgentContactsModal] = useState(false);
  const isAgentPage = page?.account?.type === ACCOUNT_TYPE_AGENT;
  const systemWide = page?.systemWide;
  const isUrlTypeAgent = type === ACCOUNT_TYPE_AGENT;
  const isUrlTypeOwner = type === ACCOUNT_TYPE_OWNER;
  const showBlock = (!systemWide && isUrlTypeAgent) || isUrlTypeOwner;
  const isCommunicationPresent = !isEmpty(page?.communication) && !isNil(page?.communication);
  const headerBaseIcons = [
    {
      type: 'facebook',
      onClick: () => window.open(getLinkWithProto(page.facebookLink) || 'https://www.facebook.com/safariportal'),
    },
    {
      type: 'instagram',
      onClick: () => window.open(getLinkWithProto(page.instagramLink) || 'https://www.instagram.com/safariportalapp/'),
    },
    {
      type: 'vimeo',
      onClick: () => window.open(getLinkWithProto(page.vimeoLink) || 'https://vimeo.com/user134112656'),
    },
  ];
  const headerAgentIcons = [
    // {
    //   type: 'share',
    //   onClick: () => {},
    // },
    // {
    //   type: 'print',
    //   onClick: () => {},
    // },
    ...(isCommunicationPresent ? [{ type: 'phone', onClick: () => setShowAgentContactsModal(true) }] : []),
  ];

  const icons = isAgentPage ? headerAgentIcons : headerBaseIcons;
  const closeAgentContactsModal = () => setShowAgentContactsModal(false);
  const isRemoveBrandingAllowed = isCheckSubscriptionPermissions(page.subscriptions, PERMISSION_REMOVE_BRANDING);
  return {
    ...defaultValue,
    landingData,
    page,
    links,
    readOnly: true,
    hasHeader: showBlock,
    isPortal: true,
    headerOptions: {
      userName: 'Guest',
      logoSrc: page.account?.logo?.url,
      companyName: page.account?.companyName,
      brandLogoSrc: page.brandSettings?.logo?.url,
      brandCompanyName: page.brandSettings?.companyName,
      whiteLabelingOn: page?.whiteLabelingOn,
      whiteLabelingDisplayCompanyLogo: page?.whiteLabelingDisplayCompanyLogo,
      isAgentPage,
      icons,
      iconsParams: {
        margin: 'mini',
      },
    },
    hasNavNotch: links.length > 1,
    hasPoweredBy: !isRemoveBrandingAllowed,
    showAgentContactsModal,
    closeAgentContactsModal,
  };
}

export function preparePageData(data) {
  const { type } = queryString.parse(window.location.search);
  const pageData = clone(data);
  const isProperty = pageData?.type === 'property';
  const systemWide = pageData?.systemWide;
  const isUrlTypeAgent = type === ACCOUNT_TYPE_AGENT;
  const isUrlTypeOwner = type === ACCOUNT_TYPE_OWNER;
  const showBlock = (!systemWide && !isUrlTypeAgent) || isUrlTypeOwner;

  if (!isProperty) return pageData;

  // Location block
  if (pageData.latitude && pageData.longitude) {
    pageData.landing.pages[0].blocks.push(
      {
        type: 'header',
        data: { subheadline: 'Location' },
      },
      {
        type: 'location',
        data: {
          latitude: pageData.latitude,
          longitude: pageData.longitude,
        },
      },
    );
  }

  // Contacts block
  if (showBlock && (pageData?.emergencyContacts || pageData?.commonContacts)) {
    const emergencyContacts = pageData.emergencyContacts?.filter(({ email, phone }) => email || phone) || [];
    const commonContacts = pageData.commonContacts?.filter(({ email, phone }) => email || phone) || [];
    const result = [];

    if (emergencyContacts.length > 0) {
      result.push({
        type: 'Emergency Contacts',
        items: emergencyContacts,
      });
    }

    if (commonContacts.length > 0) {
      result.push({
        type: 'Office Сontacts',
        items: commonContacts,
      });
    }

    if (result.length > 0) {
      pageData.landing.pages[0].blocks.push(
        {
          type: 'header',
          data: { subheadline: 'Contacts' },
        },
        {
          type: 'contacts',
          data: {
            types: result,
          },
        },
      );
    }
  }

  // Rate and Info sheets block
  if (showBlock && pageData.documents?.length > 0) {
    pageData.landing.pages[0].blocks.push(
      {
        type: 'header',
        data: { subheadline: 'Rate and Info Sheets' },
      },
      {
        type: 'sheets',
        data: {
          documents: pageData.documents,
        },
      },
    );
  }

  if (!pageData.landing.pages[0].title) {
    pageData.landing.pages[0].title = pageData.name;
  }

  // Gallery block
  const galleryPhotos = collectGalleryPhotos(pageData);

  if (galleryPhotos.length > 0) {
    pageData.landing.pages[0].blocks.push(
      {
        type: 'header',
        data: { subheadline: 'Gallery', mountNodeId: 'gallery' },
      },
      {
        type: 'gallery',
        data: {
          photos: galleryPhotos,
        },
      },
    );
  }

  return pageData;
}

const isNoData = (value) => isNil(value) || isEmpty(value) || Number.isNaN(value);

export function collectGalleryPhotos(pageData = {}) {
  let res = [];

  if (pageData.primaryImageId) {
    res.push(pageData.primaryImageId);
  }

  const blocks = pageData.landing?.pages?.[0]?.blocks;
  if (blocks) {
    blocks.forEach((block) => {
      if (block.type === 'image' && !isNoData(block.data?.files)) {
        res = res.concat(block.data?.files.map((f) => f.id));
      }
    });
  }

  if (!isNoData(pageData.media)) {
    res = res.concat(pageData.media.map((m) => m.id));
  }

  if (!isNoData(pageData?.rooms)) {
    pageData.rooms.forEach((r) => {
      if (!isNoData(r?.media)) {
        res = res.concat(r.media.map((m) => m.id));
      }
    });
  }

  return uniq(res);
}

function getLinkWithProto(link) {
  if (!isNil(link) && !/^https?:\/\//.test(link)) return 'https://' + link;

  return link;
}

export function getLandingForViewMode(page, options) {
  const { alwaysHideImageBlocks = false, isProposalPreview = false } = options || {};
  page = clone(page);

  if (!isDefined(page?.landing?.pages)) {
    return page;
  }

  page.landing.pages = page.landing.pages.map((p) => {
    if (isDefined(p.blocks)) {
      p.blocks = reduceBlocks(p.blocks, { alwaysHideImageBlocks });
    }

    return p;
  });

  const filteredPages = isProposalPreview ? getVisibleProposalPages(page) : page.landing.pages;
  return { ...page, landing: { pages: filteredPages } };
}

function reduceBlocks(blocks, options) {
  // clearing empty blocks
  let result = blocks.reduce((res, curBlock) => {
    if (isDefined(curBlock.children)) {
      curBlock.children = reduceBlocks(curBlock.children, options);
    }

    switch (curBlock.type) {
      case BASIC_BLOCK_TYPES.HEADER:
        if (isDefined(curBlock?.data?.subheadline)) {
          res.push(curBlock);
        }
        break;
      case BASIC_BLOCK_TYPES.TEXT:
        if (isTextBlockDefined(curBlock?.data)) {
          res.push(curBlock);
        }
        break;
      case BASIC_BLOCK_TYPES.LIST:
        if (isDefined(curBlock?.data?.items)) {
          curBlock = clone(curBlock);
          curBlock.data.items = curBlock.data.items.filter((it) => isFastFactItemDefined(it));
        }

        if (isAnyFastFactItemDefined(curBlock?.data)) {
          res.push(curBlock);
        }
        break;
      case BASIC_BLOCK_TYPES.ACCORDION:
        if (isDefined(curBlock?.data?.spoilers)) {
          curBlock = clone(curBlock);
          curBlock.data.spoilers = curBlock.data.spoilers.filter((it) => isAccordionItemDefined(it));
        }

        if (isAnyAccordionItemDefined(curBlock?.data)) {
          res.push(curBlock);
        }
        break;
      case BASIC_BLOCK_TYPES.IMAGE:
        if (!options.alwaysHideImageBlocks && isDefined(curBlock?.data)) {
          res.push(curBlock);
        }
        break;
      case BASIC_BLOCK_TYPES.DOCUMENT:
        if (isDefined(curBlock?.data)) {
          res.push(curBlock);
        }
        break;
      case BASIC_BLOCK_TYPES.ACCOMMODATIONS:
        res.push(curBlock);
        break;
      case BASIC_BLOCK_TYPES.EMBED:
        if (isDefined(curBlock?.data)) {
          res.push(curBlock);
        }
        break;
      default:
        res.push(curBlock);
    }

    return res;
  }, []);

  return result;
}
